/**
 * Contractor Company Portal - Lightweight Dashboard
 * 
 * DESIGN PRINCIPLES:
 * - Minimal clutter, maximum action
 * - Fast load, instant clarity
 * - Enterprise feel, contractor focus
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  Building2,
  Briefcase,
  AlertTriangle,
  Clock,
  FileText,
  RefreshCw,
  LogOut,
  ChevronRight,
  ChevronDown,
  Phone,
  Upload,
  ArrowRight,
  CheckCircle,
  MapPin,
  Zap,
  Check,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import axios from 'axios';
import { StatusChip } from '@/components/phs/StatusChip';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('contractor_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============================================================
// 🎨 DESIGN TOKENS (Enterprise - Teal + Slate)
// ============================================================
const tokens = {
  primary: "#0d9488",     // teal-600
  primaryHover: "#0f766e", // teal-700
  primaryDark: "#0f172a",  // slate-900 (navy)
  bg: "#f8fafc",           // slate-50
  card: "#FFFFFF",
  textMain: "#0f172a",     // slate-900
  textMuted: "#64748b",    // slate-500
  border: "#e2e8f0",       // slate-200
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
};

// ============================================================
// 🧱 ENTERPRISE COMPONENTS
// ============================================================

const Card = ({ children, className = "", hover = false, onClick }) => (
  <motion.div
    whileHover={hover ? { y: -2 } : {}}
    onClick={onClick}
    className={`
      bg-white border border-slate-200 rounded-2xl p-4 shadow-sm
      ${hover ? "cursor-pointer hover:shadow-md hover:border-teal-300 transition-all" : ""}
      ${className}
    `}
  >
    {children}
  </motion.div>
);

const PrimaryButton = ({ children, onClick, loading, disabled, className = "" }) => {
  const [done, setDone] = useState(false);

  const handleClick = async () => {
    if (loading || disabled || done) return;
    await onClick?.();
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };

  return (
    <motion.button
      whileHover={!loading && !done && !disabled ? { scale: 1.01 } : {}}
      whileTap={!loading && !done && !disabled ? { scale: 0.98 } : {}}
      onClick={handleClick}
      disabled={disabled || loading}
      className={`
        w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200
        flex items-center justify-center gap-2
        ${done 
          ? "bg-emerald-500 text-white" 
          : "bg-teal-700 text-white hover:bg-teal-800"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
      `}
    >
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.span key="loading" className="flex items-center gap-2">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
            />
            Processing...
          </motion.span>
        ) : done ? (
          <motion.span key="done" initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> Done
          </motion.span>
        ) : (
          <motion.span key="label" className="flex items-center gap-2">
            {children}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

const SecondaryButton = ({ children, onClick, icon: Icon, className = "" }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`
      px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium
      bg-white hover:bg-slate-50 hover:border-slate-300 transition-all
      text-slate-700
      flex items-center justify-center gap-2
      ${className}
    `}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </motion.button>
);

const StatCard = ({ title, value, subtitle, icon: Icon, variant = "default", onClick }) => {
  const variants = {
    default: "border-slate-200",
    warning: "border-amber-300 bg-amber-50/50",
    danger: "border-red-300 bg-red-50/50",
    success: "border-emerald-300 bg-emerald-50/50",
    primary: "border-teal-300 bg-teal-50/50",
  };

  const iconVariants = {
    default: "bg-gray-100 text-gray-600",
    warning: "bg-amber-100 text-amber-600",
    danger: "bg-red-100 text-red-600",
    success: "bg-emerald-100 text-emerald-600",
    primary: "bg-teal-100 text-teal-700",
  };

  const valueColors = {
    default: "text-slate-900",
    warning: "text-amber-600",
    danger: "text-red-600",
    success: "text-emerald-600",
    primary: "text-teal-700",
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        bg-white border-2 rounded-2xl p-4 shadow-sm cursor-pointer
        hover:shadow-md transition-all
        ${variants[variant]}
      `}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-[#64748B] uppercase tracking-wide">{title}</p>
          <motion.p 
            className={`text-3xl font-bold mt-1 ${valueColors[variant]}`}
            key={value}
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            {value}
          </motion.p>
          {subtitle && <p className="text-xs text-[#64748B] mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-xl ${iconVariants[variant]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay }}
  >
    {children}
  </motion.div>
);

// ============================================================
// 🔥 CURRENT MISSION COMPONENT
// ============================================================
const CurrentMission = ({ activeJobs, quotesNeeded }) => {
  let mission = "All caught up!";
  let urgency = "default";
  let icon = Check;
  let subtitle = "No immediate actions required";

  if (quotesNeeded > 0) {
    mission = `Submit ${quotesNeeded} quote${quotesNeeded > 1 ? 's' : ''}`;
    urgency = "warning";
    icon = FileText;
    subtitle = "Quotes awaiting submission";
  }
  
  if (activeJobs > 0) {
    mission = `Complete ${activeJobs} active job${activeJobs > 1 ? 's' : ''}`;
    urgency = "primary";
    icon = Briefcase;
    subtitle = "Jobs in progress";
  }

  const bgColors = {
    default: "bg-gradient-to-br from-emerald-50 to-emerald-100/30 border-emerald-200",
    warning: "bg-gradient-to-br from-amber-50 to-amber-100/30 border-amber-200",
    primary: "bg-gradient-to-br from-teal-50 to-teal-100/30 border-teal-300",
  };

  const textColors = {
    default: "text-emerald-700",
    warning: "text-amber-700",
    primary: "text-teal-700",
  };

  const Icon = icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border-2 p-5 relative overflow-hidden ${bgColors[urgency]}`}
    >
      <motion.div 
        className="absolute top-3 right-3 opacity-30"
        animate={{ rotate: [0, 5, -5, 0] }}
        transition={{ repeat: Infinity, duration: 4 }}
      >
        <Icon className="h-12 w-12" />
      </motion.div>
      
      <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider flex items-center gap-1">
        <Zap className="h-3 w-3" /> Current Focus
      </div>
      <div className={`text-2xl font-bold mt-1 ${textColors[urgency]}`}>
        {mission}
      </div>
      <div className="text-sm text-[#64748B] mt-1">{subtitle}</div>
    </motion.div>
  );
};

// ============================================================
// 🟦 ACTIVE JOB CARD
// ============================================================
const ActiveJobCard = ({ job, onAction }) => (
  <Card hover className="relative">
    {/* Status indicator using StatusChip */}
    <div className="absolute top-4 right-4">
      <StatusChip status={job.status} size="small" />
    </div>

    <div className="font-semibold text-[#0F172A]">{job.job_reference}</div>
    <div className="text-sm text-[#64748B] flex items-center gap-1">
      <MapPin className="h-3 w-3" />
      {job.park_name || job.site_name} {job.plot && `• Plot ${job.plot}`}
    </div>

    <p className="mt-3 text-sm text-[#64748B] line-clamp-2">{job.description}</p>

    {/* Primary Action */}
    <div className="mt-4">
      <PrimaryButton onClick={() => onAction(job, job.status === "approved" ? "start" : "complete")}>
        {job.status === "approved" ? "Start Job" : "Mark Complete"}
        <ArrowRight className="h-4 w-4" />
      </PrimaryButton>
    </div>

    {/* Quick Actions - simplified */}
    <div className="flex gap-2 mt-3">
      <SecondaryButton icon={Phone} onClick={() => onAction(job, "call")} className="flex-1">
        Call
      </SecondaryButton>
      <SecondaryButton icon={Upload} onClick={() => onAction(job, "upload")} className="flex-1">
        Upload
      </SecondaryButton>
    </div>
  </Card>
);

// ============================================================
// 🟨 OTHER JOB CARD (Simplified with StatusChip)
// ============================================================
const OtherJobCard = ({ job, onAction }) => {
  const getActionLabel = () => {
    const status = (job.status || '').toLowerCase();
    if (status.includes('assigned') || status.includes('sent')) return "Accept";
    if (status.includes('quote') && !status.includes('submitted')) return "Quote";
    if (status.includes('submitted')) return "View";
    return "View";
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center justify-between py-3 px-4 bg-white border border-slate-200 rounded-xl hover:border-teal-300 transition-all cursor-pointer"
      onClick={() => onAction(job, "view")}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 truncate">{job.job_reference}</span>
          <StatusChip status={job.status} size="small" />
        </div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3" />
          {job.park_name || job.site_name}
        </div>
      </div>
      
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={(e) => { e.stopPropagation(); onAction(job, "view"); }}
        className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-xl hover:bg-teal-800 transition-colors flex items-center gap-1"
      >
        {getActionLabel()}
        <ChevronRight className="h-4 w-4" />
        <ChevronRight className="h-4 w-4" />
      </motion.button>
    </motion.div>
  );
};

// ============================================================
// 🎯 MAIN COMPONENT
// ============================================================
export default function ContractorCompanyDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [showOtherJobs, setShowOtherJobs] = useState(false);

  const contractorName = localStorage.getItem('contractor_portal_name') || 'Contractor Portal';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashRes = await api.get('/contractor-company/dashboard');
      setDashboard(dashRes.data.data);
    } catch (err) {
      console.error('Load error:', err);
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('contractor_portal_token');
    if (!token) {
      navigate('/contractor-company/login');
      return;
    }
    loadData();
  }, [loadData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('contractor_portal_token');
    localStorage.removeItem('contractor_portal_id');
    localStorage.removeItem('contractor_portal_name');
    navigate('/contractor-company/login');
  };

  const handleJobAction = (job, action) => {
    console.log("Job action:", job.job_reference, action);
    navigate(`/contractor-company/jobs/${job.id || job.internal_id}`);
  };

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
          <Loader2 className="w-8 h-8 text-teal-600" />
        </motion.div>
      </div>
    );
  }

  const headline = dashboard?.headline || {};
  const activeJobs = dashboard?.jobs?.filter(j => ["approved", "in_progress"].includes(j.status)) || [];
  const otherJobs = dashboard?.jobs?.filter(j => !["approved", "in_progress"].includes(j.status)) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ═══════════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════════ */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-700 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">{contractorName}</h1>
              <p className="text-xs text-slate-500">Contractor Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadData}
              disabled={loading}
              className="p-2 rounded-xl hover:bg-gray-100 text-slate-500 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="p-2 rounded-xl hover:bg-gray-100 text-slate-500 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════
          NAVIGATION
      ═══════════════════════════════════════════════════════ */}
      <nav className="bg-white border-b-2 border-slate-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1">
            <Link 
              to="/contractor-company/dashboard" 
              className="px-4 py-3 text-sm font-semibold text-teal-700 border-b-2 border-teal-700"
            >
              Dashboard
            </Link>
            <Link 
              to="/contractor-company/jobs" 
              className="px-4 py-3 text-sm font-medium text-slate-500 hover:text-slate-900 border-b-2 border-transparent transition-colors"
            >
              All Jobs
            </Link>
            <Link 
              to="/contractor-company/performance" 
              className="px-4 py-3 text-sm font-medium text-slate-500 hover:text-slate-900 border-b-2 border-transparent transition-colors"
            >
              Performance
            </Link>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════
          CONTENT
      ═══════════════════════════════════════════════════════ */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* 🔥 CURRENT MISSION */}
        <FadeIn>
          <CurrentMission 
            activeJobs={headline.jobs_awaiting_completion || 0}
            quotesNeeded={headline.quotes_awaiting_submission || 0}
          />
        </FadeIn>

        {/* METRICS GRID */}
        <FadeIn delay={0.05}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Jobs"
              value={headline.total_jobs || 0}
              icon={Briefcase}
              variant="default"
              onClick={() => navigate('/contractor-company/jobs')}
            />
            <StatCard
              title="Overdue"
              value={headline.overdue_jobs || 0}
              icon={AlertTriangle}
              variant={headline.overdue_jobs > 0 ? "danger" : "success"}
              onClick={() => navigate('/contractor-company/jobs?overdue=true')}
            />
            <StatCard
              title="Quotes Needed"
              value={headline.quotes_awaiting_submission || 0}
              icon={FileText}
              variant={headline.quotes_awaiting_submission > 0 ? "warning" : "default"}
              onClick={() => navigate('/contractor-company/jobs?status=AWAITING_QUOTE')}
            />
            <StatCard
              title="In Progress"
              value={headline.jobs_awaiting_completion || 0}
              icon={Clock}
              variant="primary"
              onClick={() => navigate('/contractor-company/jobs?status=IN_PROGRESS')}
            />
          </div>
        </FadeIn>

        {/* 🟦 ACTIVE JOBS */}
        <FadeIn delay={0.1}>
          <div>
            <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Zap className="h-4 w-4 text-teal-600" />
              Active Jobs ({activeJobs.length})
            </h2>
            
            {activeJobs.length > 0 ? (
              <div className="space-y-4">
                {activeJobs.slice(0, 3).map((job, i) => (
                  <motion.div
                    key={job.id || job.internal_id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <ActiveJobCard job={job} onAction={handleJobAction} />
                  </motion.div>
                ))}
                {activeJobs.length > 3 && (
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    onClick={() => navigate('/contractor-company/jobs?status=IN_PROGRESS')}
                    className="w-full py-3 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-xl transition-colors"
                  >
                    View all {activeJobs.length} active jobs →
                  </motion.button>
                )}
              </div>
            ) : (
              <Card>
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Check className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-slate-500">No active jobs right now</p>
                </div>
              </Card>
            )}
          </div>
        </FadeIn>

        {/* 🟨 OTHER JOBS (COLLAPSIBLE) */}
        {otherJobs.length > 0 && (
          <FadeIn delay={0.15}>
            <div>
              <motion.button
                whileHover={{ backgroundColor: "rgb(249 250 251)" }}
                onClick={() => setShowOtherJobs(!showOtherJobs)}
                className="w-full flex justify-between items-center p-3 bg-white border-2 border-slate-200 rounded-xl transition-colors"
              >
                <span className="text-sm font-semibold text-slate-900">
                  Other Jobs ({otherJobs.length})
                </span>
                <motion.div animate={{ rotate: showOtherJobs ? 180 : 0 }}>
                  <ChevronDown className="h-5 w-5 text-slate-500" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {showOtherJobs && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mt-3 space-y-2"
                  >
                    {otherJobs.slice(0, 5).map((job, i) => (
                      <OtherJobCard key={job.id || i} job={job} onAction={handleJobAction} />
                    ))}
                    {otherJobs.length > 5 && (
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        onClick={() => navigate('/contractor-company/jobs')}
                        className="w-full py-3 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-xl transition-colors"
                      >
                        View all {otherJobs.length} jobs →
                      </motion.button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeIn>
        )}

        {/* ⚙ QUICK ACTIONS */}
        <FadeIn delay={0.2}>
          <Card>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contractor-company/jobs')}
                className="p-4 border-2 border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all flex flex-col items-center gap-2"
              >
                <Briefcase className="w-6 h-6 text-teal-700" />
                <span className="text-sm font-medium text-slate-900">All Jobs</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contractor-company/jobs?status=AWAITING_QUOTE')}
                className="p-4 border-2 border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all flex flex-col items-center gap-2"
              >
                <FileText className="w-6 h-6 text-teal-700" />
                <span className="text-sm font-medium text-slate-900">Submit Quotes</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contractor-company/jobs?status=COMPLETED')}
                className="p-4 border-2 border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all flex flex-col items-center gap-2"
              >
                <Receipt className="w-6 h-6 text-teal-700" />
                <span className="text-sm font-medium text-slate-900">Invoices</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contractor-company/performance')}
                className="p-4 border-2 border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all flex flex-col items-center gap-2"
              >
                <TrendingUp className="w-6 h-6 text-teal-700" />
                <span className="text-sm font-medium text-slate-900">Performance</span>
              </motion.button>
            </div>
          </Card>
        </FadeIn>
      </main>
    </div>
  );
}
